<template>
    <div class="block-editor task-update">
        <section>
            <div class="wrapper">
                <div class="editor-row-title">Task ID</div>
                <TiptapEditor
                    placeholder="Task ID"
                    :show-attributes="true"
                    v-model="clone.taskId"
                    automation-mode
                />
            </div>
        </section>
        <section>
            <div class="title">Updateable Fields</div>
            <div class="fields-wrapper">
                <div v-for="(field, index) in clone.fields" :key="index" class="field-row">
                    <div class="field-container">
                        <label>Field</label>
                        <CustomPicker
                            v-model="field.field"
                            :items="availableFields"
                            idKey="name"
                            labelKey="label"
                            title="Select Field"
                            @input="onFieldSelect(field)"
                        />
                    </div>
                    <div class="value-container">
                        <div class="value-header">
                            <label>Value</label>
                            <div class="mode-switch" v-if="canBeDynamic(field.field)">
                                <span class="switch-label">Dynamic</span>
                                <a-switch
                                    size="small"
                                    :checked="field.mode === 'dynamic'"
                                    @change="(checked) => onModeChange(checked, field)"
                                />
                            </div>
                        </div>
                        <template v-if="field.mode === 'static'">
                            <!-- Pipeline Stage Picker -->
                            <CustomPicker
                                v-if="field.field === 'pipelineStage'"
                                v-model="field.value"
                                :items="pipelineStages"
                                idKey="_id"
                                labelKey="title"
                                :placeholder="getPlaceholderForField(field.field)"
                            />
                            
                            <!-- Boolean Switches -->
                            <a-switch
                                v-else-if="['completed', 'done', 'archived'].includes(field.field)"
                                v-model="field.value"
                            />

                            <!-- Priority Picker -->
                            <CustomPicker
                                v-else-if="field.field === 'priority'"
                                v-model="field.value"
                                :items="priorityOptions"
                                idKey="value"
                                labelKey="label"
                                :placeholder="getPlaceholderForField(field.field)"
                            />

                            <!-- User Pickers (Multiple) -->
                            <CustomPicker
                                v-else-if="['assignees', 'collaborators', 'followers'].includes(field.field)"
                                v-model="field.value"
                                :items="userOptions"
                                idKey="_id"
                                labelKey="name"
                                :placeholder="getPlaceholderForField(field.field)"
                                mode="multiple"
                            />

                            <!-- Owner Picker -->
                            <CustomPicker
                                v-else-if="field.field === 'owner'"
                                v-model="field.value"
                                :items="userOptions"
                                idKey="_id"
                                labelKey="name"
                                :placeholder="getPlaceholderForField(field.field)"
                            />

                            <!-- Tags Picker -->
                            <CustomPicker
                                v-else-if="field.field === 'tags'"
                                v-model="field.value"
                                :items="tagOptions"
                                idKey="_id"
                                labelKey="title"
                                :placeholder="getPlaceholderForField(field.field)"
                                mode="multiple"
                            />
                        </template>
                        <template v-else>
                            <TiptapEditor
                                v-model="field.value"
                                :placeholder="getPlaceholderForField(field.field)"
                                automation-mode
                                :show-attributes="true"
                            />
                        </template>
                    </div>

                    <SupsisButton @click="removeField(index)" icon danger xsmall>
                        <i class="material-icons">delete</i>
                    </SupsisButton>
                </div>
                <a-button type="primary" @click="addField">
                    <a-icon type="plus" />
                    Add Field
                </a-button>
            </div>
        </section>
    </div>
</template>

<script>
import _ from "lodash";
import TiptapEditor from "../../../../Chatbot/pages/dashboard/common/TiptapEditor.vue";
import { mapState } from "vuex";
import CustomPicker from "../../../../../../components/pickers/CustomPicker.vue";
import SupsisButton from "../../../../Settings/components/SupsisButton.vue";

export default {
    name: "UpdateTask",
    components: {
        TiptapEditor,
        CustomPicker,
        SupsisButton,
    },
    props: ["value"],
    data() {
        return {
            clone: _.cloneDeep(this.value || { fields: [] }),
            availableFields: [
                { name: "pipelineStage", label: "Pipeline Stage" },
                { name: "completed", label: "Completed" },
                { name: "title", label: "Title" },
                { name: "description", label: "Description" },
                { name: "priority", label: "Priority" },
                { name: "assignees", label: "Assignees" },
                { name: "collaborators", label: "Collaborators" },
                { name: "followers", label: "Followers" },
                { name: "owner", label: "Owner" },
                { name: "done", label: "Done" },
                { name: "archived", label: "Archived" },
                { name: "tags", label: "Tags" },
                { name: "endDate", label: "End Date" },
            ],
            priorityOptions: [
                { value: "lowest", label: "Lowest" },
                { value: "low", label: "Low" },
                { value: "medium", label: "Medium" },
                { value: "high", label: "High" },
                { value: "highest", label: "Highest" },
            ],
        };
    },
    computed: {
        ...mapState({
            users: (state) => state.user.users.data,
            pipelineStages: (state) => state.tasks.workflows.map(workflow => workflow.pipeline.map(stage => ({
                _id: stage._id,
                title: `${stage.name} Stage on ${workflow.title} workflow`,
                workflowId: workflow._id,
            }))).flat(),
            tags: (state) => state.task.tags.data.filter(tag => tag.type === "task")
        }),
        userOptions() {
            return this.users.map(user => ({
                _id: user._id,
                name: `${user.name} (${user.email})`
            }));
        },
        tagOptions() {
            return this.tags.map(tag => ({
                _id: tag._id,
                title: tag.name
            }));
        }
    },
    methods: {
        addField() {
            this.clone.fields.push({ 
                field: null, 
                value: null, 
                mode: 'static' 
            });
        },
        removeField(index) {
            this.clone.fields.splice(index, 1);
        },
        canBeDynamic(fieldName) {
            const dynamicFields = ['priority', 'owner', 'title', 'description', 'endDate'];
            return dynamicFields.includes(fieldName);
        },
        onlyDynamic(fieldName) {
            return ['title', 'description', 'endDate'].includes(fieldName);
        },
        onlyStatic(fieldName) {
            return ['pipelineStage', 'completed', 'assignees', 'collaborators', 'followers', 'done', 'archived', 'tags'].includes(fieldName);
        },
        getPlaceholderForField(field) {
            const placeholders = {
                pipelineStage: "Select Pipeline Stage",
                priority: "Select Priority",
                assignees: "Select Assignees",
                collaborators: "Select Collaborators",
                followers: "Select Followers",
                owner: "Select Owner",
                tags: "Select Tags",
                title: "Enter Title",
                description: "Enter Description",
                endDate: "Enter End Date"
            };
            return placeholders[field] || "";
        },
        onFieldSelect(field) {
            if (this.onlyDynamic(field.field)) {
                field.mode = 'dynamic';
            } else if (this.onlyStatic(field.field)) {
                field.mode = 'static';
            }
            
            if (field.value === null) {
                if (['completed', 'done', 'archived'].includes(field.field)) {
                    field.value = false;
                }
            }
        },
        onModeChange(checked, field) {
            field.mode = checked ? 'dynamic' : 'static';
            field.value = null;
        }
    },
    watch: {
        clone: {
            deep: true,
            handler(newValue) {
                this.$emit("input", newValue);
            },
        },
    },
};
</script>

<style lang="scss">
.block-editor.task-update {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 30px;

    section {
        margin-bottom: 10px;
    }

    .title {
        margin-bottom: 10px;
        font-weight: 600;
        font-size: 16px;
        line-height: 20px;
    }

    .fields-wrapper {
        .field-row {
            display: flex;
            gap: 10px;
            margin-bottom: 10px;
            align-items: center;
            border-bottom: 1px solid #e0e0e0;

            > * {
                flex: 1;
            }

            .field-container,
            .value-container {
                display: flex;
                flex-direction: column;
            }

            .value-container {
                .value-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 8px;

                    .mode-switch {
                        display: flex;
                        align-items: center;
                        gap: 8px;

                        .switch-label {
                            font-size: 12px;
                            color: rgba(0, 0, 0, 0.65);
                        }
                    }
                }
            }
        }
    }
}
</style>
